import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { OrderDetailDialog } from "@bcc/components/orders/order-detail-dialog/order-detail-dialog";
import { exportOrdersToCsv } from "@bcc/components/orders/utils/orders-export";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bcc/components/ui/alert-dialog";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { EmptyValue } from "@bcc/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import type { CarrierCustomerAccountBinding, LabelPrintBatchCreateRequest, LabelPrintBatchDetail, PurchaseOrder } from "@shared/types";
import type { RowSelectionState } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Calendar, Download, Package, Printer, ShoppingBag, Weight } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BatchOrdersTable } from "./batch-orders-table";
import { BatchSummaryActions } from "./batch-summary-actions";
import {
  getBatchStatusBadgeClassName,
  getBatchStatusLabel,
  getBatchStatusLabelKey,
  getReadableBatchId,
  getRetryBatchRequest,
  isBatchProcessing,
  setBatchSummaryBatchIdTestId,
  setBatchSummaryContainerTestId,
  setBatchSummaryExportButtonTestId,
  setBatchSummaryExportDialogTestId,
  setBatchSummaryMetricTestId,
  setBatchSummaryStatusTestId,
} from "./batch-summary.utils";

interface BatchSummaryProps {
  readonly batch?: LabelPrintBatchDetail | null;
  readonly customerAccountCarrierDetails?: CarrierCustomerAccountBinding[];
  readonly isLoading?: boolean;
  readonly isRetrying?: boolean;
  readonly isGeneratingManifest?: boolean;
  readonly onGenerateManifest?: (batch: LabelPrintBatchDetail) => void;
  readonly onReprintLabels?: (batch: LabelPrintBatchDetail) => void;
  readonly onReprintPackingSlips?: (batch: LabelPrintBatchDetail) => void;
  readonly onReprintLabelsAndPackingSlips?: (batch: LabelPrintBatchDetail) => void;
  readonly onOpenSummary?: (batch: LabelPrintBatchDetail) => void;
  readonly onRetryBatch?: (request: LabelPrintBatchCreateRequest) => void | Promise<void>;
  readonly testId?: string;
}

const formatBatchDate = (value: string | undefined, t: ReturnType<typeof useTranslation>["t"]): string => {
  if (!value) {
    return "—";
  }

  try {
    const date = parseISO(value);
    return `${t("dates.monthdayyear", { date })} ${t("dates.time", { date })}`;
  } catch {
    return value;
  }
};

export function BatchSummary({
  batch,
  customerAccountCarrierDetails,
  isLoading = false,
  isRetrying = false,
  isGeneratingManifest = false,
  onGenerateManifest,
  onReprintLabels,
  onReprintPackingSlips,
  onReprintLabelsAndPackingSlips,
  onOpenSummary,
  onRetryBatch,
  testId,
}: BatchSummaryProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isExportConfirmOpen, setIsExportConfirmOpen] = useState(false);

  const handleOrderClick = useCallback((order: PurchaseOrder) => {
    setSelectedOrder(order);
    setOrderDialogOpen(true);
  }, []);

  const handleOrderDialogOpenChange = useCallback((open: boolean) => {
    setOrderDialogOpen(open);
    if (!open) {
      setSelectedOrder(null);
    }
  }, []);

  const handleConfirmExport = useCallback(() => {
    if (!batch) return;
    const selectedIds = new Set(
      Object.entries(rowSelection)
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => id)
    );
    const ordersToExport =
      selectedIds.size > 0 ? batch.purchaseOrders.filter(({ purchaseOrderId }) => selectedIds.has(purchaseOrderId)) : batch.purchaseOrders;
    exportOrdersToCsv(ordersToExport, t);
    setIsExportConfirmOpen(false);
    setRowSelection({});
  }, [batch, rowSelection, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset row selection and confirm-dialog state when the active batch changes
    setRowSelection({});
    setIsExportConfirmOpen(false);
  }, [batch?.batchId]);

  const handleBatchLinkFromOrderDialog = useCallback(
    (batchId: string) => {
      navigate({
        pathname: ROUTE_PATHS.BATCHES,
        search: `?batchId=${batchId}`,
      });
    },
    [navigate]
  );

  if (isLoading) {
    return (
      <Card testId={testId ?? setBatchSummaryContainerTestId()} data-tour-id={TOUR_IDS.batches.summary} className="border-primary/20">
        <CardContent className="p-6">
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-sm text-muted-foreground">
            {t("dataTable.loading")}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!batch) {
    return (
      <Card testId={testId ?? setBatchSummaryContainerTestId()} data-tour-id={TOUR_IDS.batches.summary} className="border-primary/20">
        <CardContent className="p-6">
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-sm text-muted-foreground">
            {t("views.batchDetailModal.description")}
          </div>
        </CardContent>
      </Card>
    );
  }

  const statusLabelKey = getBatchStatusLabelKey(batch.batchStatus);
  const statusLabel = getBatchStatusLabel(batch.batchStatus);
  const statusBadgeClassName = getBatchStatusBadgeClassName(batch.batchStatus);
  const batchDate = formatBatchDate(batch.createdAt, t);
  const readableId = getReadableBatchId(batch.batchId);
  const hasTotalWeight = batch.totalWeightInGrams !== undefined;
  const isExportDisabled = batch.purchaseOrders.length === 0;
  const totalWeight = batch.totalWeightInGrams ?? 0;
  const retryRequest = getRetryBatchRequest(batch);

  return (
    <Card testId={testId ?? setBatchSummaryContainerTestId()} data-tour-id={TOUR_IDS.batches.summary} className="border-primary/20">
      <CardContent className="space-y-6 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-semibold tracking-tight" data-testid={setBatchSummaryBatchIdTestId()}>
                {readableId}
              </h2>
              <Badge variant="secondary" className={statusBadgeClassName} data-testid={setBatchSummaryStatusTestId()}>
                {statusLabelKey ? t(`views.batchStatus.${statusLabelKey}`) : statusLabel}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t("views.batchSummary.created")}: {batchDate}
              </span>
              <span>
                {t("views.batchDetailModal.totalOrders")}: {batch.totalOrders}
              </span>
              {batch.batchNotes && <span className="block max-w-2xl truncate">{batch.batchNotes}</span>}
            </div>
          </div>

          <BatchSummaryActions
            batch={batch}
            customerAccountCarrierDetails={customerAccountCarrierDetails}
            isProcessing={isBatchProcessing(batch)}
            isRetrying={isRetrying}
            isGeneratingManifest={isGeneratingManifest}
            retryRequest={retryRequest}
            onGenerateManifest={onGenerateManifest}
            onReprintLabels={onReprintLabels}
            onReprintPackingSlips={onReprintPackingSlips}
            onReprintLabelsAndPackingSlips={onReprintLabelsAndPackingSlips}
            onOpenSummary={onOpenSummary}
            onRetryBatch={onRetryBatch}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-tour-id={TOUR_IDS.batches.summaryMetrics}>
          <MetricCard
            icon={<Package className="h-5 w-5 text-primary" />}
            label={t("views.batchSummary.totalOrders")}
            value={batch.totalOrders}
            testId={setBatchSummaryMetricTestId("orders")}
          />
          <MetricCard
            icon={<ShoppingBag className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
            label={t("views.batchSummary.totalUnits")}
            value={batch.totalUnits}
            testId={setBatchSummaryMetricTestId("units")}
          />
          <MetricCard
            icon={<Weight className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
            label={t("views.batchSummary.totalWeight")}
            value={hasTotalWeight ? t("weight.weightWithSuffix", { grams: totalWeight }) : <EmptyValue />}
            testId={setBatchSummaryMetricTestId("weight")}
          />
          <MetricCard
            icon={<Printer className="h-5 w-5 text-green-600 dark:text-green-400" />}
            label={t("views.batchSummary.labelsPrinted")}
            value={batch.labelCreatedOrders}
            testId={setBatchSummaryMetricTestId("labels-printed")}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {t("views.batchDetailModal.ordersInBatch")}
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={isExportDisabled}
                    data-testid={setBatchSummaryExportButtonTestId()}
                    onClick={() => setIsExportConfirmOpen(true)}
                    aria-label={t("views.batchOrdersTable.exportConfirmation.confirm")}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("views.batchOrdersTable.exportConfirmation.confirm")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <BatchOrdersTable
            purchaseOrders={batch.purchaseOrders}
            onOrderClick={handleOrderClick}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            areRowsDisabled={true}
          />
        </div>
      </CardContent>

      <OrderDetailDialog
        purchaseOrder={selectedOrder}
        open={orderDialogOpen}
        onOpenChange={handleOrderDialogOpenChange}
        onBatchClick={handleBatchLinkFromOrderDialog}
      />

      <AlertDialog open={isExportConfirmOpen} onOpenChange={setIsExportConfirmOpen}>
        <AlertDialogContent testId={setBatchSummaryExportDialogTestId("content")}>
          <AlertDialogHeader>
            <AlertDialogTitle data-testid={setBatchSummaryExportDialogTestId("title")}>
              {t("views.batchOrdersTable.exportConfirmation.title")}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid={setBatchSummaryExportDialogTestId("description")}>
              {t("views.batchOrdersTable.exportConfirmation.message", {
                count: Object.values(rowSelection).filter(Boolean).length || batch.purchaseOrders.length,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel testId={setBatchSummaryExportDialogTestId("cancel")}>{t("buttons.cancel")}</AlertDialogCancel>
            <AlertDialogAction testId={setBatchSummaryExportDialogTestId("confirm")} onClick={handleConfirmExport}>
              {t("views.batchOrdersTable.exportConfirmation.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  testId: string;
}

function MetricCard({ icon, label, value, testId }: MetricCardProps) {
  return (
    <Card className="border-border" testId={testId}>
      <CardContent className="flex items-center gap-4 p-4">
        <div className="rounded-lg bg-muted/60 p-3">{icon}</div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold">{value}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
